// U1c-3 — Add SLink Device. Mirrors UserInvite.tsx's ephemeral no-persist
// one-time-secret surface (the enrollment token lives ONLY in component state),
// adding a copy button + a non-recoverable warning. Reuses the existing
// useSites() for the site dropdown (no new sites endpoint).
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useCreateSlinkAgent } from "@/api/agents";
import { useSites } from "@/api/sites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenantStore } from "@/store/tenant";

export default function SlinkAgentCreatePage() {
  const [name, setName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [credentialType, setCredentialType] = useState("jwt");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // SECURITY: the enrollment token lives ONLY here, in ephemeral component
  // state — never localStorage/sessionStorage, never a query cache, never
  // logged. Cleared on "Add Another" and gone on unmount.
  const [created, setCreated] = useState<{
    id: string;
    name: string;
    status: string;
    enrollment_token: string;
  } | null>(null);

  const create = useCreateSlinkAgent();
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const needsTenant = isSuperAdmin && !currentTenantId;
  const { data: sitesData, isLoading: sitesLoading } = useSites();
  const sites = sitesData?.items ?? [];
  const nav = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Enter a device name");
      return;
    }
    if (!siteId) {
      setError("Pick a site");
      return;
    }
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        site_id: siteId,
        credential_type: credentialType,
      });
      setCreated({
        id: result.id,
        name: result.name,
        status: result.status,
        enrollment_token: result.enrollment_token,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response
        ?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      if (status === 409) {
        setError(
          "This site already has a SLink agent. Choose another site or rotate the existing agent's token.",
        );
      } else if (status === 404) {
        setError("Selected site is not available.");
      } else if (status === 403) {
        setError("You don't have permission to register devices.");
      } else {
        setError(
          typeof detail === "string" ? detail : "Failed to register device",
        );
      }
    }
  }

  async function copyToken() {
    if (!created) return;
    try {
      await navigator.clipboard?.writeText(created.enrollment_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / denied) — the operator can
      // still select the mono-font token block manually. No token is logged.
    }
  }

  if (created) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-3 p-4">
            <p className="text-base font-semibold text-emerald-700">
              SLink device “{created.name}” registered
            </p>
            <p className="text-xs text-muted-foreground">
              Agent ID <span className="font-mono">{created.id}</span> · status{" "}
              {created.status}
            </p>
            <div>
              <Label>Device key</Label>
              <p className="mt-1 break-all rounded bg-muted/40 p-2 font-mono text-xs">
                {created.enrollment_token}
              </p>
              <div className="mt-2 flex gap-2">
                <Button variant="outline" size="sm" onClick={copyToken}>
                  {copied ? "Copied" : "Copy key"}
                </Button>
              </div>
            </div>
            <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
              This device key is shown once and cannot be retrieved again.
              Copy it now and deliver it to the device out-of-band. If lost,
              rotate the key to issue a new one.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreated(null);
                  setName("");
                  setSiteId("");
                  setCopied(false);
                }}
              >
                Add Another
              </Button>
              <Button onClick={() => nav("/devices/slink")}>
                Back to Devices
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Add SLink Device
        </h1>
        <p className="text-sm text-muted-foreground">
          Registers an edge agent for one site and issues a one-time enrollment
          token.
        </p>
      </div>
      {needsTenant ? (
        <p className="text-sm text-muted-foreground">
          Select a tenant from the switcher to register a device.
        </p>
      ) : (
        <Card>
          <CardContent className="p-4">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <Label>Device name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Site</Label>
                <select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  required
                  className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                >
                  <option value="">
                    {sitesLoading ? "Loading sites…" : "Choose a site…"}
                  </option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {!sitesLoading && sites.length === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    No sites yet. Create a site before registering a device.
                  </p>
                )}
              </div>
              <div>
                <Label>Credential type</Label>
                <select
                  value={credentialType}
                  onChange={(e) => setCredentialType(e.target.value)}
                  className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                >
                  <option value="jwt">JWT (default)</option>
                  <option value="token">Token</option>
                  <option value="mtls">mTLS</option>
                </select>
              </div>
              {error && (
                <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" asChild>
                  <Link to="/devices/slink">Cancel</Link>
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? "Registering…" : "Register device"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
